/**
 * Message text to React nodes (never HTML strings: rule 5).
 *
 * Blocks (renderMarkdown, docs/design/visual-overhaul.md §7.9 P2): ``` code
 * (a header with its language and Copy, a small keyword lexer), > quotes,
 * - and 1. lists, # ## ### headings. Inline (tokenize / renderTokens):
 * `code`, **bold**, *italic*, __underline__, ~~strike~~, ||spoilers||, plain
 * links (no unfurl yet), mentions <@user>, <@&role>, <#channel>,
 * @everyone / @here, and emoji (our bundled images where we have them;
 * custom server emoji <:name:id> / <a:name:id> as their images).
 *
 * Previews (search results, pins) render inline only, code as "[code]". The
 * row around a preview is not a button, so the mentions and links in it stay
 * buttons and links of their own, never nested in another (I20, P26).
 */
import type { Snowflake } from '@neosocial/protocol';
import { useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { tr } from '../i18n';
import { roleColorPair } from '../store/selectors';
import { Icon } from '../ui/kit';
import { toast } from '../ui/overlay';
import { Emoji, emojiNodes } from './emoji';
import { customName } from './customEmoji';

export interface MentionResolver {
  user(id: Snowflake): string | null;
  role(id: Snowflake): { name: string; color: number } | null;
  channel(id: Snowflake): string | null;
  meId?: Snowflake;
  /**
   * The message really mentioned everyone (its `mention_everyone`): only
   * then is @everyone / @here drawn as a mention. Typed by someone without
   * Mention Everyone it pinged nobody, so it stays text (Discord).
   */
  mentionEveryone?: boolean;
  onUser?(id: Snowflake, el: HTMLElement): void;
  onChannel?(id: Snowflake): void;
}

export interface RenderOpts {
  /** Inline only, code as "[code]": search results and pins. */
  preview?: boolean;
}

type Tok =
  | { k: 'text'; v: string }
  | { k: 'code'; v: string }
  | { k: 'pre'; v: string; lang: string }
  | { k: 'link'; v: string }
  | { k: 'user'; v: string }
  | { k: 'role'; v: string }
  | { k: 'chan'; v: string }
  | { k: 'every'; v: string }
  | { k: 'cemoji'; v: string }
  | { k: 'bold'; v: Tok[] }
  | { k: 'ital'; v: Tok[] }
  | { k: 'under'; v: Tok[] }
  | { k: 'strike'; v: Tok[] }
  | { k: 'spoil'; v: Tok[] };

/** A ``` fence: an optional language on the opening line, then the code (Discord). */
function fence(inner: string): { v: string; lang: string } {
  let body = inner;
  let lang = '';
  const first = /^([A-Za-z0-9_+#.-]{1,24})\n/.exec(body);
  if (first) {
    lang = first[1]!;
    body = body.slice(first[0].length);
  } else if (body.startsWith('\n')) body = body.slice(1);
  if (body.endsWith('\n')) body = body.slice(0, -1);
  return { v: body, lang };
}

const RULES: Array<[RegExp, (m: RegExpExecArray) => Tok]> = [
  [/```([\s\S]+?)```/y, (m) => ({ k: 'pre', ...fence(m[1]!) })],
  [/`([^`\n]+)`/y, (m) => ({ k: 'code', v: m[1]! })],
  [/<a?:[A-Za-z0-9_]{1,32}:\d{1,20}>/y, (m) => ({ k: 'cemoji', v: m[0] })],
  [/<@!?(\d+)>/y, (m) => ({ k: 'user', v: m[1]! })],
  [/<@&(\d+)>/y, (m) => ({ k: 'role', v: m[1]! })],
  [/<#(\d+)>/y, (m) => ({ k: 'chan', v: m[1]! })],
  [/@(everyone|here)\b/y, (m) => ({ k: 'every', v: m[1]! })],
  [/https?:\/\/[^\s<>]+[^\s<>.,:;!?'")\]]/y, (m) => ({ k: 'link', v: m[0] })],
  [/\|\|([\s\S]+?)\|\|/y, (m) => ({ k: 'spoil', v: tokenize(m[1]!) })],
  [/\*\*([\s\S]+?)\*\*/y, (m) => ({ k: 'bold', v: tokenize(m[1]!) })],
  [/__([^_\n][\s\S]*?)__/y, (m) => ({ k: 'under', v: tokenize(m[1]!) })],
  [/~~([^~\n][\s\S]*?)~~/y, (m) => ({ k: 'strike', v: tokenize(m[1]!) })],
  [/\*([^*\s][^*]*?)\*/y, (m) => ({ k: 'ital', v: tokenize(m[1]!) })],
];

/**
 * The characters a rule can start with (each rule's first character): every
 * other character is plain text, so the twelve regexes run only where one
 * of them could match (chat performance, 2026-11-01: they ran at every
 * character of every message on every render).
 */
const STARTS = new Set(
  RULES.map(([re]) => (re.source[0] === '\\' ? re.source[1]! : re.source[0]!)),
);

export function tokenize(src: string): Tok[] {
  const out: Tok[] = [];
  // the plain text since the last token: [from, i)
  let from = 0;
  let i = 0;
  outer: while (i < src.length) {
    if (STARTS.has(src[i]!)) {
      for (const [re, mk] of RULES) {
        re.lastIndex = i;
        const m = re.exec(src);
        if (m) {
          // read the end first: mk() may tokenize the inside, which reuses (and moves) these sticky regexes
          const end = re.lastIndex;
          if (i > from) out.push({ k: 'text', v: src.slice(from, i) });
          out.push(mk(m));
          i = from = end;
          continue outer;
        }
      }
    }
    i++;
  }
  if (i > from) out.push({ k: 'text', v: src.slice(from, i) });
  return out;
}

/** What a mention the reader can't resolve shows instead of its raw ID. */
function unknownLabel(kind: 'user' | 'role' | 'chan'): string {
  const { t } = tr();
  return t(
    kind === 'user'
      ? 'ns.mention.unknownUser'
      : kind === 'role'
        ? 'ns.mention.deletedRole'
        : 'ns.mention.noAccess',
  );
}

/**
 * A click inside a message's text does only what was clicked: a mention,
 * a link or a spoiler never also opens the row around it (a search
 * result, a pinned message).
 */
const own = (e: MouseEvent) => e.stopPropagation();

function Spoiler({ children, label }: { children: ReactNode; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className={'spoiler' + (open ? ' open' : '')}
      role="button"
      tabIndex={0}
      aria-label={open ? undefined : label}
      aria-expanded={open}
      onClick={(e) => {
        if (!open) e.stopPropagation();
        setOpen(true);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }
      }}
    >
      <span>{children}</span>
    </span>
  );
}

/* ------------------------------------------------------------ code blocks */

const KEYWORDS: Record<string, string> = {
  js: 'const let var function return if else for while do break continue new class extends import from export default async await try catch finally throw typeof instanceof in of switch case this null undefined true false yield delete void interface type enum implements as',
  py: 'def return if elif else for while break continue class import from as with try except finally raise lambda pass yield None True False and or not in is global nonlocal async await',
  sh: 'if then else elif fi for in do done while until case esac function return export local echo cd exit set unset source sudo',
  json: 'true false null',
};
const LANGS: Record<string, string> = {
  js: 'js',
  javascript: 'js',
  jsx: 'js',
  mjs: 'js',
  cjs: 'js',
  ts: 'js',
  typescript: 'js',
  tsx: 'js',
  py: 'py',
  python: 'py',
  sh: 'sh',
  bash: 'sh',
  shell: 'sh',
  zsh: 'sh',
  console: 'sh',
  json: 'json',
  jsonc: 'json',
};
const LEX: Record<string, RegExp> = {
  js: /(\/\/[^\n]*|\/\*[\s\S]*?\*\/)|("(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*'|`(?:\\.|[^`\\])*`)|(\b(?:0x[\da-f]+|\d+(?:\.\d+)?(?:e[+-]?\d+)?)\b)|([A-Za-z_$][\w$]*)/gi,
  py: /(#[^\n]*)|("(?:\\.|[^"\\\n])*"|'(?:\\.|[^'\\\n])*')|(\b(?:0x[\da-f]+|\d+(?:\.\d+)?(?:e[+-]?\d+)?)\b)|([A-Za-z_][\w]*)/gi,
  sh: /((?:^|(?<=\s))#[^\n]*)|("(?:\\.|[^"\\])*"|'[^']*')|(\$\{?[\w@#?*!-]+\}?)|([A-Za-z_][\w-]*)/gm,
  json: /()("(?:\\.|[^"\\\n])*")|(-?\b\d+(?:\.\d+)?(?:e[+-]?\d+)?\b)|([A-Za-z_]\w*)/gi,
};

/** A 60-line keyword lexer for js/ts, py, sh and json (§7.9): spans in the --syn-* colours. */
export function highlight(code: string, lang: string): ReactNode[] {
  const l = LANGS[lang.toLowerCase()];
  if (!l) return [code];
  const kw = new Set(KEYWORDS[l]!.split(' '));
  const re = new RegExp(LEX[l]!.source, LEX[l]!.flags);
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  const span = (cls: string, v: string) => (
    <span key={n++} className={cls}>
      {v}
    </span>
  );
  for (let m = re.exec(code); m; m = re.exec(code)) {
    if (!m[0]) {
      re.lastIndex++;
      continue;
    }
    if (m.index > last) out.push(code.slice(last, m.index));
    last = m.index + m[0].length;
    const [all, com, str, num, word] = m;
    if (com) out.push(span('syn-com', all));
    else if (str) {
      // a JSON key is a string followed by a colon
      const key = l === 'json' && /^\s*:/.test(code.slice(last));
      out.push(span(key ? 'syn-key' : 'syn-str', all));
    } else if (num) out.push(span(l === 'sh' ? 'syn-key' : 'syn-num', all));
    else if (word && kw.has(word)) out.push(span('syn-kw', all));
    else if (word && l !== 'json' && /^\s*\(/.test(code.slice(last))) out.push(span('syn-fn', all));
    else out.push(all);
  }
  if (last < code.length) out.push(code.slice(last));
  return out;
}

function CodeBlock({ code, lang }: { code: string; lang: string }) {
  const { t } = tr();
  const copy = (e: MouseEvent) => {
    e.stopPropagation();
    const failed = () => toast(t('ns.msg.copyFailed'), { icon: 'alert', tone: 'warn' });
    if (!navigator.clipboard) return failed();
    void navigator.clipboard
      .writeText(code)
      .then(() => toast(t('c.copied'), { icon: 'check' }), failed);
  };
  return (
    <div className="code" dir="ltr">
      <div className="code-h">
        <span className="code-lang">{lang || t('chat.code.plain')}</span>
        <button type="button" className="code-copy" onClick={copy}>
          <Icon name="copy" size="s" />
          {t('chat.code.copy')}
        </button>
      </div>
      {/* the code as written: <pre><code>, left to right, its own scroller */}
      <pre data-lang={lang || undefined}>
        <code>{highlight(code, lang)}</code>
      </pre>
    </div>
  );
}

/* ------------------------------------------------------------ inline */

export function renderTokens(
  toks: Tok[],
  r: MentionResolver,
  spoilerLabel: string,
  key = 'k',
  opts: RenderOpts = {},
): ReactNode[] {
  const preview = !!opts.preview;
  return toks.map((t, i) => {
    const k = `${key}-${i}`;
    switch (t.k) {
      case 'text': {
        const lines = t.v.split(String.fromCharCode(10));
        if (lines.length === 1) return emojiNodes(t.v, k);
        return lines.flatMap((l, j) =>
          j ? [<br key={k + '-br' + j} />, ...emojiNodes(l, k + '-' + j)] : emojiNodes(l, k + '-0'),
        );
      }
      case 'cemoji':
        return <Emoji key={k} e={t.v} />;
      case 'code':
        return (
          <code key={k} className="ic">
            {t.v}
          </code>
        );
      case 'pre':
        return preview ? (
          <span key={k} className="ns-code-ph">
            {tr().t('chat.code.preview')}
          </span>
        ) : (
          <CodeBlock key={k} code={t.v} lang={t.lang} />
        );
      case 'link':
        return (
          <a
            key={k}
            className="lnk"
            href={t.v}
            target="_blank"
            rel="noopener noreferrer nofollow"
            dir="ltr"
            onClick={own}
          >
            {t.v}
          </a>
        );
      case 'user': {
        const name = r.user(t.v);
        if (name === null) {
          return (
            <span key={k} className="mention ns-mention-gone">
              <bdi>@{unknownLabel('user')}</bdi>
            </span>
          );
        }
        const cls = 'mention' + (t.v === r.meId ? ' me' : '');
        // one isolate with its "@": in a right-to-left line a bare "@"
        // took the line's direction and read "Ava Stone@" (QA 2026-10-28)
        return (
          <button
            key={k}
            type="button"
            className={cls}
            onClick={(e) => {
              e.stopPropagation();
              r.onUser?.(t.v, e.currentTarget);
            }}
          >
            <bdi>@{name}</bdi>
          </button>
        );
      }
      case 'role': {
        const role = r.role(t.v);
        let style: CSSProperties | undefined;
        if (role?.color) {
          const [l, d] = roleColorPair(role.color);
          style = { '--rc-l': l, '--rc-d': d } as CSSProperties;
        }
        return (
          <span
            key={k}
            className={'mention role' + (style ? ' ns-rc' : '') + (role ? '' : ' ns-mention-gone')}
            style={style}
          >
            <bdi>@{role?.name ?? unknownLabel('role')}</bdi>
          </span>
        );
      }
      case 'chan': {
        const name = r.channel(t.v);
        if (name === null) {
          // hidden from this reader (or deleted): never its ID, never a link that does nothing
          return (
            <span key={k} className="mention chan ns-mention-gone">
              <Icon name="lock" />
              <bdi>{unknownLabel('chan')}</bdi>
            </span>
          );
        }
        return (
          <button
            key={k}
            type="button"
            className="mention chan"
            onClick={(e) => {
              e.stopPropagation();
              r.onChannel?.(t.v);
            }}
          >
            <Icon name="hash" />
            <bdi>{name}</bdi>
          </button>
        );
      }
      case 'every':
        if (!r.mentionEveryone) return '@' + t.v;
        return (
          <span key={k} className="mention all">
            @{t.v}
          </span>
        );
      case 'bold':
        return <strong key={k}>{renderTokens(t.v, r, spoilerLabel, k, opts)}</strong>;
      case 'ital':
        return <em key={k}>{renderTokens(t.v, r, spoilerLabel, k, opts)}</em>;
      case 'under':
        return <u key={k}>{renderTokens(t.v, r, spoilerLabel, k, opts)}</u>;
      case 'strike':
        return <s key={k}>{renderTokens(t.v, r, spoilerLabel, k, opts)}</s>;
      case 'spoil':
        return (
          <Spoiler key={k} label={spoilerLabel}>
            {renderTokens(t.v, r, spoilerLabel, k, opts)}
          </Spoiler>
        );
    }
  });
}

/* ------------------------------------------------------------ blocks */

type Block =
  | { k: 'p'; v: string }
  | { k: 'pre'; v: string; lang: string }
  | { k: 'h'; level: 1 | 2 | 3; v: string }
  | { k: 'quote'; v: string }
  | { k: 'list'; ordered: boolean; start: number; items: string[] };

const HEAD = /^(#{1,3}) +(\S.*)$/;
const QUOTE = /^> ?(.*)$/;
const BULLET = /^[ \t]*[-*] +(\S.*)$/;
const NUMBER = /^[ \t]*(\d{1,3})[.)] +(\S.*)$/;

/** The line-level structure of one stretch of text (no fences in it). */
function lineBlocks(src: string, out: Block[]): void {
  const lines = src.split('\n');
  let para: string[] = [];
  const flush = () => {
    if (para.length) out.push({ k: 'p', v: para.join('\n') });
    para = [];
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    let m = HEAD.exec(line);
    if (m) {
      flush();
      out.push({ k: 'h', level: m[1]!.length as 1 | 2 | 3, v: m[2]! });
      continue;
    }
    if (QUOTE.test(line)) {
      flush();
      const q: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) q.push(QUOTE.exec(lines[i++]!)![1]!);
      i--;
      out.push({ k: 'quote', v: q.join('\n') });
      continue;
    }
    const b = BULLET.exec(line);
    m = b ? null : NUMBER.exec(line);
    if (b || m) {
      flush();
      const ordered = !b;
      const items: string[] = [];
      const start = m ? Number(m[1]) : 1;
      for (; i < lines.length; i++) {
        const x = ordered ? NUMBER.exec(lines[i]!) : BULLET.exec(lines[i]!);
        if (!x) break;
        items.push(ordered ? x[2]! : x[1]!);
      }
      i--;
      out.push({ k: 'list', ordered, start, items });
      continue;
    }
    para.push(line);
  }
  flush();
}

/** Fences first (they may hold anything), then the lines between them. */
export function parseBlocks(src: string): Block[] {
  const out: Block[] = [];
  const re = /```([\s\S]+?)```/g;
  let last = 0;
  for (let m = re.exec(src); m; m = re.exec(src)) {
    // the line break next to a fence belongs to the fence (Discord)
    const before = src.slice(last, m.index).replace(/\n$/, '');
    if (before) lineBlocks(before, out);
    out.push({ k: 'pre', ...fence(m[1]!) });
    last = m.index + m[0].length;
    if (src[last] === '\n') last++;
  }
  const rest = src.slice(last);
  if (rest || !out.length) lineBlocks(rest, out);
  return out;
}

/**
 * A whole message: blocks, each with its inline markup. User content keeps
 * its own direction: every block that holds text is dir="auto".
 */
export function renderMarkdown(
  src: string,
  r: MentionResolver,
  spoilerLabel: string,
  opts: RenderOpts = {},
): ReactNode[] {
  if (opts.preview) return renderTokens(tokenize(src), r, spoilerLabel, 'p', opts);
  const blocks = parseBlocks(src);
  // one paragraph stays inline (the "(edited)" mark follows it); next to blocks,
  // each paragraph is a block with its own direction
  const single = blocks.length === 1;
  return blocks.map((b, i) => {
    const k = 'b' + i;
    switch (b.k) {
      case 'p':
        return single ? (
          <span key={k}>{renderTokens(tokenize(b.v), r, spoilerLabel, k, opts)}</span>
        ) : (
          <div key={k} className="md-p" dir="auto">
            {renderTokens(tokenize(b.v), r, spoilerLabel, k, opts)}
          </div>
        );
      case 'pre':
        return <CodeBlock key={k} code={b.v} lang={b.lang} />;
      case 'h':
        return (
          <div key={k} className={'md-h md-h' + b.level} dir="auto">
            {renderTokens(tokenize(b.v), r, spoilerLabel, k, opts)}
          </div>
        );
      case 'quote':
        return (
          <blockquote key={k} className="md-q" dir="auto">
            {renderMarkdown(b.v, r, spoilerLabel, opts)}
          </blockquote>
        );
      case 'list': {
        const items = b.items.map((it, j) => (
          <li key={j} dir="auto">
            {renderTokens(tokenize(it), r, spoilerLabel, k + '-' + j, opts)}
          </li>
        ));
        return b.ordered ? (
          <ol key={k} className="md-list" start={b.start}>
            {items}
          </ol>
        ) : (
          <ul key={k} className="md-list">
            {items}
          </ul>
        );
      }
    }
  });
}

function tokensText(toks: Tok[], r: Pick<MentionResolver, 'user' | 'role' | 'channel'>): string {
  let out = '';
  for (const t of toks) {
    switch (t.k) {
      case 'text':
      case 'code':
      case 'pre':
      case 'link':
        out += t.v;
        break;
      case 'user':
        out += '@' + (r.user(t.v) ?? unknownLabel('user'));
        break;
      case 'role':
        out += '@' + (r.role(t.v)?.name ?? unknownLabel('role'));
        break;
      case 'chan':
        out += '#' + (r.channel(t.v) ?? unknownLabel('chan'));
        break;
      case 'every':
        out += '@' + t.v;
        break;
      case 'cemoji':
        out += `:${customName(t.v) ?? '_'}:`;
        break;
      default:
        out += tokensText(t.v, r);
    }
  }
  return out;
}

/**
 * The text a message shows, as plain text: mentions resolved to names,
 * markup gone, code kept as written. Used for reply lines, the topic,
 * notifications and Copy text.
 */
export function plainText(
  src: string,
  r: Pick<MentionResolver, 'user' | 'role' | 'channel'>,
): string {
  return tokensText(tokenize(src), r);
}
